import React from 'react';
import {Box, IconButton} from '@mui/material';
import {AnyQuestion, QuestionType} from 'crqlar-react-common/dist/models/forms/form';
import WelcomeScreenEditor from './WelcomeScreenEditor';
import MultipleChoiceEditor from './MultipleChoiceEditor';
import YesNoEditor from './YesNoEditor';
import ShortTextEditor from './ShortTextEditor';
import LongTextEditor from './LongTextEditor';
import LegalEditor from './LegalEditor';
import EndScreenEditor from './EndScreenEditor';
import DropdownEditor from "./DropdownEditor";
import PictureChoiceEditor from "./PictureChoiceEditor";
import DeleteIcon from "@mui/icons-material/Delete";
import RatingEditor from "./RatingEditor";
import UploadMediaEditor from "./UploadMediaEditor";
import {useTranslation} from "react-i18next";

interface QuestionEditorProps {
    question: AnyQuestion;
    onQuestionUpdate: (question: AnyQuestion) => void;
    onQuestionDelete?: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({question, onQuestionUpdate, onQuestionDelete}) => {
    const {t} = useTranslation();

    // Render the appropriate editor based on question type
    const renderEditor = () => {
        switch (question.type) {
            case QuestionType.WELCOME:
                return (
                    <WelcomeScreenEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.MULTIPLE_CHOICE:
                return (
                    <MultipleChoiceEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.PICTURE_CHOICE:
                return (
                    <PictureChoiceEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.YES_NO:
                return (
                    <YesNoEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.DROPDOWN:
                return (
                    <DropdownEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.SHORT_TEXT:
                return (
                    <ShortTextEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.LONG_TEXT:
                return (
                    <LongTextEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.LEGAL:
                return (
                    <LegalEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.RATING:
                return (
                    <RatingEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.UPLOAD_MEDIA:
                return (
                    <UploadMediaEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            case QuestionType.END_SCREEN:
                return (
                    <EndScreenEditor
                        question={question}
                        onQuestionUpdate={onQuestionUpdate}
                    />
                );

            default:
                return (
                    <Box sx={{p: 2, bgcolor: 'rgba(255, 255, 255, 0.2)', borderRadius: 1}}>
                        {t('Unsupported question type:')} {(question as AnyQuestion).type}
                    </Box>
                );
        }
    }

    return (
        <Box
            sx={{
                width: '100%',
                position: 'relative',
            }}
        >
            {
                !!onQuestionDelete &&
                <IconButton
                    sx={{
                        position: 'absolute',
                        top: 22,
                        right: -22,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        }
                    }}
                    onClick={(e) => onQuestionDelete()}
                    size={'small'}
                >
                    <DeleteIcon sx={{color: '#fff'}}/>
                </IconButton>
            }

            {renderEditor()}
        </Box>
    );
};

export default QuestionEditor;
